import { ThemeProvider as RMWCThemeProvider } from '@rmwc/theme'
import cloneDeep from 'lodash.clonedeep'
import isObject from 'lodash.isobject'
import merge from 'lodash.merge'
import { node, number, shape, string } from 'prop-types'
import React, { ReactElement, ReactNode, useState } from 'react'
import { useEffect } from 'react'

import { CoreComponentProps, withCoreComponent } from '../withCoreComponent'
import { RMWCThemeOption, defaultTheme } from './defaultTheme'
import { Theme } from './theme'

class Defaults {
  static propTypes = {
    theme: shape({
      type: string.isRequired,
      surface: string,
      backgroundtheme: string,
      button: shape({
        default: string,
        disabled: string,
      }),
      snackbar: shape({
        light: string,
        main: string,
        dark: string,
        text: string,
      }),
      primary: shape({
        light: string,
        main: string.isRequired,
        dark: string,
        text: string,
      }),
      secondary: shape({
        light: string,
        main: string.isRequired,
        dark: string,
        text: string,
      }),
      info: shape({
        light: string,
        main: string,
        dark: string,
        text: string,
      }),
      success: shape({
        light: string,
        main: string,
        dark: string,
        text: string,
      }),
      error: shape({
        light: string,
        main: string,
        dark: string,
        text: string,
      }),
      warning: shape({
        light: string,
        main: string,
        dark: string,
        text: string,
      }),
      text: shape({
        primary: string,
        secondary: string,
        disabled: string,
        default: string,
      }),
      divider: string,
      typography: shape({
        fontFamily: string,
        fontSize: string,
        fontWeightThin: number,
        fontWeightLight: number,
        fontWeightRegular: number,
        fontWeightMedium: number,
        fontWeightBold: number,
        fontWeightDark: number,
      }),
    }),
  }
}

export function computeStyle(obj: Theme, path = '-', style = {}) {
  for (let k in obj) {
    if (isObject(obj[k])) {
      computeStyle(obj[k], path + `-${k}`, style)
    } else {
      style[`${path}-${k}`] = obj[k]
    }
  }

  return style
}

export const ThemeContext = React.createContext(defaultTheme)

export interface ThemeProviderProps {
  children: ReactElement
  theme: Theme
}

export const ThemeProvider: React.FC<ThemeProviderProps> = (
  props: ThemeProviderProps,
) => {
  const { theme, children } = props

  const clonePalette = cloneDeep(defaultTheme)
  const cloneTheme = cloneDeep(theme)
  const mergedTheme = merge(clonePalette, cloneTheme)
  const [style, setStyle] = useState({})

  useEffect(() => {
    if (theme) {
      const rootStyle = {
        color: 'var(--text-default)',
        fontFamily: 'var(--text-default)',
        fontSize: 'var(--font-size)',
      }

      setStyle(computeStyle(mergedTheme, '-', rootStyle))
    }
  }, [theme])

  const elements = React.cloneElement(children, { style })

  return (
    <ThemeContext.Provider
      value={theme ? mergedTheme : React.useContext(ThemeContext)}
    >
      <RMWCThemeProvider options={RMWCThemeOption} wrap>
        {elements}
      </RMWCThemeProvider>
    </ThemeContext.Provider>
  )
}

ThemeProvider.propTypes = {
  ...Defaults.propTypes,
  children: node.isRequired,
} as any

ThemeProvider.defaultProps = { theme: defaultTheme }

export default withCoreComponent(ThemeProvider)
